import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import jspPlatformApi from "client/portals";
import {
  isApiError,
  isErrorResponseBodyType,
  toJspApiError,
} from "models/utils/apiUtils";
import { MenuLoopCopyParams } from "client/jspPlatformExperiment";
import { DisplayMenuLoop } from "models/menu/menuLoop";
import { getDisplayMenuLoopFromMenuLoopData } from "utils/menu/menuLoop/menuLoopDataToDisplayUtils";
import {
  getMenuLoopCreateDeepFromDisplay,
  getMenuLoopUpdateDeepFromDisplay,
} from "utils/menu/menuLoop/menuLoopDisplayToDeepUpdateUtils";
import { menuBatchOperationActions } from "./menuBatchOperationSlice";

interface MenuLoopDetailStateProps {
  menuLoop: DisplayMenuLoop;
  isLoading: boolean;
  isSaving: boolean;
  isCopying: boolean;
  error: string | null | undefined;
}

const initialState: MenuLoopDetailStateProps = {
  menuLoop: {
    id: 0,
    name: "",
    days_per_cycle: 7,
    start_date: "",
    end_date: "",
    menu_links: [],
  },
  isLoading: false,
  isSaving: false,
  isCopying: false,
  error: null,
};

/** Return value of menu loop data from the Jasper Platform API call */
const fetchAMenuLoop = createAsyncThunk(
  "menuLoopDetail/fetchAMenuLoop",
  async (fetchingMenuLoopId: number, { rejectWithValue }) =>
    jspPlatformApi()
      .menuLoop.getMenuLoopByMenuLoopId({
        path: { menu_loop_id: fetchingMenuLoopId },
      })
      .then((res) => {
        const result = res.data;
        if (!result) {
          throw new Error("No menu loop data");
        }
        return getDisplayMenuLoopFromMenuLoopData(result);
      })
      .catch((error) => {
        let errMsg: string;
        const err = isApiError(error) ? toJspApiError(error) : null;
        if (err && isErrorResponseBodyType(err.body)) {
          errMsg = err.body.detail;
        } else {
          errMsg = "Unknown error!";
        }
        return rejectWithValue(errMsg);
      }),
);

/** Create a new menu loop */
const createMenuLoop = createAsyncThunk(
  "menuLoopDetail/createMenuLoop",
  async (menuLoop: DisplayMenuLoop, { rejectWithValue }) =>
    jspPlatformApi()
      .menuLoop.postMenuLoop({
        body: getMenuLoopCreateDeepFromDisplay(menuLoop),
      })
      .then(() => {
        return "Menu loop creation succeed";
      })
      .catch((error) => {
        let errMsg: string;
        const err = isApiError(error) ? toJspApiError(error) : null;
        if (err && isErrorResponseBodyType(err.body)) {
          errMsg = err.body.detail;
        } else {
          errMsg = "Unknown error!";
        }
        return rejectWithValue(errMsg);
      }),
);

/** Update an existing menu loop */
const updateMenuLoop = createAsyncThunk(
  "menuLoopDetail/updateMenuLoop",
  async (
    { menuLoopId, menuLoop }: { menuLoopId: number; menuLoop: DisplayMenuLoop },
    { rejectWithValue },
  ) =>
    jspPlatformApi()
      .menuLoop.patchMenuLoopByMenuLoopId({
        path: { menu_loop_id: menuLoopId },
        body: getMenuLoopUpdateDeepFromDisplay(menuLoop),
      })
      .then(() => {
        return "Menu loop updating succeed";
      })
      .catch((error) => {
        let errMsg: string;
        const err = isApiError(error) ? toJspApiError(error) : null;
        if (err && isErrorResponseBodyType(err.body)) {
          errMsg = err.body.detail;
        } else {
          errMsg = "Unknown error!";
        }
        return rejectWithValue(errMsg);
      }),
);

/** Copy an existing menu loop */
const copyMenuLoop = createAsyncThunk(
  "menuLoopDetail/copyMenuLoop",
  async (
    {
      menuLoopId,
      requestBody,
    }: {
      menuLoopId: number;
      requestBody: {
        new_name: string;
        site_user_group_id: number;
        skip_copy_special_dates?: boolean;
        copy_menu_templates?: boolean;
        start_date?: string;
        end_date?: string;
      };
    },
    { rejectWithValue },
  ) =>
    jspPlatformApi()
      .menuLoop.postMenuLoopByMenuLoopId({
        path: { menu_loop_id: menuLoopId },
        body: requestBody as MenuLoopCopyParams,
      })
      .then(() => {
        return "Menu loop copying succeed";
      })
      .catch((error) => {
        let errMsg: string;
        const err = isApiError(error) ? toJspApiError(error) : null;
        if (err && isErrorResponseBodyType(err.body)) {
          errMsg = err.body.detail;
        } else {
          errMsg = "Unknown error!";
        }
        return rejectWithValue(errMsg);
      }),
);

const setMenuLoopDetail = (
  state: MenuLoopDetailStateProps,
  action: PayloadAction<DisplayMenuLoop | null>,
): MenuLoopDetailStateProps => {
  if (action.payload) {
    return { ...state, menuLoop: action.payload };
  }
  return initialState;
};

export const menuLoopDetailSlice = createSlice({
  name: "menuDetailSlice",
  initialState,
  reducers: {
    setMenuLoopDetail,
  },
  extraReducers: (builder) => {
    builder.addCase(fetchAMenuLoop.pending, (state) => {
      return { ...state, isLoading: true };
    });
    builder.addCase(fetchAMenuLoop.fulfilled, (state, action) => {
      return {
        ...state,
        ...setMenuLoopDetail(state, action),
        isLoading: false,
      };
    });
    builder.addCase(fetchAMenuLoop.rejected, (state, action) => {
      return {
        ...state,
        isLoading: false,
        error: action.payload as string,
      };
    });
    builder.addCase(createMenuLoop.pending, (state) => {
      return { ...state, isSaving: true };
    });
    builder.addCase(createMenuLoop.fulfilled, (state) => {
      return {
        ...state,
        isSaving: false,
      };
    });
    builder.addCase(createMenuLoop.rejected, (state, action) => {
      return {
        ...state,
        isSaving: false,
        error: action.payload as string,
      };
    });
    builder.addCase(updateMenuLoop.pending, (state) => {
      return { ...state, isSaving: true };
    });
    builder.addCase(updateMenuLoop.fulfilled, (state) => {
      return {
        ...state,
        isSaving: false,
      };
    });
    builder.addCase(updateMenuLoop.rejected, (state, action) => {
      return {
        ...state,
        isSaving: false,
        error: action.payload as string,
      };
    });
    builder.addCase(copyMenuLoop.pending, (state) => {
      return { ...state, isCopying: true };
    });
    builder.addCase(copyMenuLoop.fulfilled, (state) => {
      return {
        ...state,
        isCopying: false,
      };
    });
    builder.addCase(copyMenuLoop.rejected, (state, action) => {
      return {
        ...state,
        isCopying: false,
        error: action.payload as string,
      };
    });
    builder.addCase(
      menuBatchOperationActions.batchUpdateMenus.fulfilled,
      (state, action) => {
        // Update menus in menu_links if they match any of the updated menus
        const updatedMenuMap = new Map(
          action.payload.map((menu) => [menu.id, menu]),
        );
        const updatedMenuLinks = state.menuLoop.menu_links.map((menuLink) => {
          const updatedMenu = updatedMenuMap.get(menuLink.menu.id);
          if (updatedMenu) {
            return {
              ...menuLink,
              menu: updatedMenu,
            };
          }
          return menuLink;
        });
        return {
          ...state,
          menuLoop: {
            ...state.menuLoop,
            menu_links: updatedMenuLinks,
          },
        };
      },
    );
  },
});

export const menuLoopActions = {
  ...menuLoopDetailSlice.actions,
  fetchAMenuLoop,
  createMenuLoop,
  updateMenuLoop,
  copyMenuLoop,
};

const menuLoopDetailReducer = menuLoopDetailSlice.reducer;

export default menuLoopDetailReducer;
